import {Injectable} from '@angular/core';
import {ThreadService} from '../../../notification-center/services/thread.service';
import {LoaderService} from '@shared/service/loader.service';
import {CommoditiesService} from '@shared/service/commodities.service';
import {NCReaderItem, NCReadersModel} from '../../../notification-center/models/nc-readers.model';
import {NemoModuleKeys, UserPermissionsModel} from '@shared/models/user-permission.model';
import {PermissionService} from '@shared/service/permission.service';
import {AuthorityUsersPermissions} from '../../../permissions-variables';
import {InnerMessage, InnerMessagesService} from '@shared/service/inner-messages.service';
import {NotificationCenterUtilities} from '../../../notification-center/utilities/notification-center.utilities';
import {Location} from '@angular/common';

export enum AuthorityEntityType {
	DOSSIER = 'DOSSIER',
	WORKING = 'WORKING',
	ORDER = 'ORDER',
	PART = 'PART'
}

@Injectable({
	providedIn: 'root'
})
export class AlertService {

	private readerMapCongifFromEntityAndSender: Map<AuthorityEntityType, Map<NCReaderItem, NCReaderItem[]>> = new Map([
		[AuthorityEntityType.WORKING, new Map([
			[NCReadersModel.SERVICE_POINT, [NCReadersModel.ALD_OPERATOR]],
			[NCReadersModel.ALD_OPERATOR, [NCReadersModel.ALD_SUPERVISOR, NCReadersModel.SERVICE_POINT]],
			[NCReadersModel.ALD_SUPERVISOR, [NCReadersModel.ALD_OPERATOR, NCReadersModel.SERVICE_POINT]]
		])],
		[AuthorityEntityType.DOSSIER, new Map([
			[NCReadersModel.SERVICE_POINT, [NCReadersModel.ALD_OPERATOR]],
			[NCReadersModel.ALD_OPERATOR, [NCReadersModel.ALD_SUPERVISOR, NCReadersModel.SERVICE_POINT]],
			[NCReadersModel.ALD_SUPERVISOR, [NCReadersModel.ALD_OPERATOR, NCReadersModel.SERVICE_POINT]]
		])],
		[AuthorityEntityType.PART, new Map([
			[NCReadersModel.SERVICE_POINT, [NCReadersModel.ALD_OPERATOR, NCReadersModel.DEALER]],
			[NCReadersModel.ALD_OPERATOR, [NCReadersModel.ALD_SUPERVISOR, NCReadersModel.SERVICE_POINT, NCReadersModel.DEALER]],
			[NCReadersModel.ALD_SUPERVISOR, [NCReadersModel.ALD_OPERATOR, NCReadersModel.SERVICE_POINT, NCReadersModel.DEALER]],
			[NCReadersModel.DEALER, [NCReadersModel.ALD_OPERATOR, NCReadersModel.SERVICE_POINT]]
		])],
		[AuthorityEntityType.ORDER, new Map([
			[NCReadersModel.DEALER, [NCReadersModel.ALD_OPERATOR, NCReadersModel.SERVICE_POINT]],
		])],
	]);

	constructor(
		private threadService: ThreadService,
		private commodityService: CommoditiesService,
		private loaderService: LoaderService,
		private innerMessagesService: InnerMessagesService,
		private permissionService: PermissionService,
		private location: Location) {
	}

	getAvailableReaderFromEntity(entity: AuthorityEntityType): NCReaderItem[] {
		return this.readerMapCongifFromEntityAndSender.get(entity).get(this.getSender(this.permissionService.getCurrentUserPermissions()));
	}

	private getSender(userPermission: UserPermissionsModel): NCReaderItem {
		if (userPermission.isAldOperator(NemoModuleKeys.AUTHORITY) && userPermission.hasPermissions(AuthorityUsersPermissions.authority_supervisor_dashboard)) {
			return NCReadersModel.ALD_SUPERVISOR;
		}
		if (userPermission.isAldOperator(NemoModuleKeys.AUTHORITY)) {
			return NCReadersModel.ALD_OPERATOR;
		}
		if (userPermission.isCenterOperator(NemoModuleKeys.AUTHORITY)) {
			return NCReadersModel.SERVICE_POINT;
		}
		if (userPermission.isDealerOperator(NemoModuleKeys.AUTHORITY)) {
			return NCReadersModel.DEALER;
		}
		return null;
	}

	writeNewAlert(module: string, ald_order_id: any, plate: string, chassis: string, commodity_id: string[], isImportant?: boolean, metadata?: AlertAuthorityMetadata, availableReaders?: NCReaderItem[], alertContext?: AuthorityEntityType): void {
		if (this.permissionService.getCurrentUserPermissions().isCenterSuperUserOperator(NemoModuleKeys.AUTHORITY)) {
			this.innerMessagesService.pushInnerMessage(new InnerMessage(InnerMessage.SUPER_USER_GATEWAY_1002, InnerMessage.TYPE_WARNING, null, true));
		} else {
			this.loaderService.display(true);
			this.generateNewAlert(module, ald_order_id, plate, chassis, commodity_id, isImportant, metadata, availableReaders, alertContext);
		}
	}

	/*private setAldCommodityIdAndGenerateNewAlert(module: string, ald_order_id: any, plate: string, chassis: string, commodity_id: string[], isImportant: boolean, metadata?: any, availableReaders?: NCReaderItem[]): void {
		this.commodityService.getCommodityById(commodity_id).pipe(catchError(error => {
			this.loaderService.display(false);
			return throwError(error);
		})).subscribe(commodity => {
			this.generateNewAlert(module, ald_order_id, plate, chassis, commodity.ald_commodity_id.toString(), commodity_id, isImportant, metadata, availableReaders);
		});
	}*/

	private generateNewAlert(module: string, ald_order_id: any, plate: string, chassis: string,
							commodity_id: string[], isImportant: boolean, metadata?: AlertAuthorityMetadata, availableReaders?: NCReaderItem[], alertContext?: AuthorityEntityType): void {
		this.threadService.setPreCompletedAlert(
			this.threadService.generateNewAlert(module, ald_order_id, plate, chassis, null, commodity_id, isImportant, metadata)
		);
		this.threadService.setAvailableReaders(availableReaders);
		this.threadService.setContext(alertContext.toString());
		this.threadService.setPreviousLocation(this.location.path());
		this.threadService.setSender(this.getSender(this.permissionService.getCurrentUserPermissions()));
		this.threadService.navigateToNewAlertPage().then(() => this.loaderService.display(false));
	}
}

export interface AlertAuthorityMetadata {
	working_type?: string;
	working_number?: string;
	part_code?: string;
	part_description?: string;
	contract_id?: string;
	dossier_number?: string;
	order_number?: string;
	dossier_status?: string;
	[NotificationCenterUtilities.REDIRECT_URL_PREFIX]?: string;
	[NotificationCenterUtilities.REDIRECT_URL_UUID]?: string;
}
